import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import App from './App';
import { allowedParentOriginsList } from './config/appRegistry';
import { createMsalConfiguration, SSO_SESSION_KEYS } from './config/msalConfig';
import { clearSsoSessionStorage, redirectParentWithIdToken, redirectParentWithError } from './utils/ssoNavigation';
import { isOAuthRedirectReturn } from './utils/oauthReturnUrl';

const msalConfig = createMsalConfiguration();
const msalInstance = new PublicClientApplication(msalConfig);

let root;
function renderApp() {
  const el = document.getElementById('root');
  if (!root) {
    root = createRoot(el);
  }
  // No StrictMode: dev double-mount can duplicate effects and confuse MSAL interactive calls.
  root.render(
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>
  );
}

msalInstance
  .initialize()
  .then(async () => {
    let response = null;
    try {
      response = await msalInstance.handleRedirectPromise();
    } catch (e) {
      const parentOrigin = sessionStorage.getItem(SSO_SESSION_KEYS.parentOrigin);
      const returnPathRaw = sessionStorage.getItem(SSO_SESSION_KEYS.returnPath);
      if (parentOrigin) {
        redirectParentWithError(
          parentOrigin,
          returnPathRaw,
          e?.errorMessage || e?.message || String(e)
        );
        return;
      }
      clearSsoSessionStorage();
      renderApp();
      return;
    }

    // Returned from /callback without a token (denied, bad state, expired code).
    if (isOAuthRedirectReturn() && !response?.idToken) {
      const parentOrigin = sessionStorage.getItem(SSO_SESSION_KEYS.parentOrigin);
      const returnPathRaw = sessionStorage.getItem(SSO_SESSION_KEYS.returnPath);
      if (parentOrigin) {
        redirectParentWithError(
          parentOrigin,
          returnPathRaw,
          'Microsoft sign-in did not complete. Please try Sign in with Microsoft again from Hermes.'
        );
        return;
      }
    }

    if (response?.idToken) {
      const parentOrigin = sessionStorage.getItem(SSO_SESSION_KEYS.parentOrigin);
      const returnPathRaw = sessionStorage.getItem(SSO_SESSION_KEYS.returnPath);
      if (parentOrigin) {
        redirectParentWithIdToken(parentOrigin, returnPathRaw, response.idToken);
        return;
      }
      const fallback = allowedParentOriginsList[0];
      if (fallback) {
        redirectParentWithError(
          fallback,
          '/',
          'Sign-in session was lost (e.g. new tab or storage cleared). Open Sign in with Microsoft from Hermes again.'
        );
        return;
      }
    }

    renderApp();
  })
  .catch((err) => {
    const parentOrigin = sessionStorage.getItem(SSO_SESSION_KEYS.parentOrigin);
    const returnPathRaw = sessionStorage.getItem(SSO_SESSION_KEYS.returnPath);
    if (parentOrigin) {
      redirectParentWithError(
        parentOrigin,
        returnPathRaw,
        err?.errorMessage || err?.message || String(err)
      );
      return;
    }
    clearSsoSessionStorage();
    renderApp();
  });
